import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { debounce } from "lodash";
import { COLOR_SYSTEM } from "../../design/design-system";
import Text from "../../atoms/Text";
import { Reference, HFF_MODULE_NAME_MAP } from "../../../common/models/types";
import DrugLabelInfo from "../../molecules/DrugLabelInfo";
import ContentSearchBar from "../../molecules/ContentSearchBar";
import ReferenceFilterBar from "./ReferenceFilterBar";
import { log, LogLevel } from "../../../utils/LogUtil";
import TabContentWrapper from "./TabContentWrapper";
import BEERS2023 from "../../../assets/docs/BEERS2023";
import CCMM from "../../../assets/docs/CCMM";
import LCMM from "../../../assets/docs/LCMM";
import SPSTv3 from "../../../assets/docs/SPSTv3";
import POLY from "../../../assets/docs/POLY";
import KABS from "../../../assets/docs/KABS";
import KDGERI from "../../../assets/docs/KIDS_GERI";
import {
  getDocumentTypeFromRefId,
  getTabNameFromDocType,
  DOC_NAME,
} from "../../../utils/documentTypeUtils";
import { eventLog } from "utils/AmplitudeUtil";
import { ToastManager } from "../../organisms/CommonUI";
import EvaluationResult from "./EvaluationResult";
import SelectionBasedReferenceButton from "./SelectionBasedReferenceButton";
import AIReviewFeedbackForm from "../../molecules/AIReviewFeedbackForm";
import { useFeedbackOptional } from "../../../contexts/FeedbackContext";

// 탭 이름에 따른 색상 결정
const getTabColor = (tabName: string) => {
  if (tabName === "평가결과") {
    return COLOR_SYSTEM.get("Red")?.[500];
  }
  if (tabName === "허가사항") {
    return COLOR_SYSTEM.get("Blue")?.[500];
  }
  if (tabName === "건기식") {
    return COLOR_SYSTEM.get("Red")?.[500];
  }
  if (tabName === "피드백") {
    return COLOR_SYSTEM.get("Gray")?.[800];
  }

  // DOC_NAME에서 해당하는 키를 찾기
  const docKey = Object.keys(DOC_NAME).find((key) => DOC_NAME[key] === tabName);

  switch (docKey) {
    case "BEERS2023":
      return COLOR_SYSTEM.get("Orange")?.[500];
    case "SPSTv3":
      return COLOR_SYSTEM.get("Green")?.[500];
    case "CCMM":
    case "LCMM":
      return COLOR_SYSTEM.get("Purple")?.[500];
    case "POLY":
      return COLOR_SYSTEM.get("Brown")?.[500];
    case "KDGERI":
      return COLOR_SYSTEM.get("Yellow")?.[500];
    default:
      return COLOR_SYSTEM.get("Skyblue")?.[500]; // 기본 색상
  }
};

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const EvidenceTabsWrap = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 0.5px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
`;

const EvidenceTabsScrollArea = styled.div`
  flex: 1;
  overflow-x: auto;
`;

const EvidenceTabs = styled.div`
  display: flex;
  white-space: nowrap;
  height: 40px;
`;

const EvidenceTab = styled.button<{ $active: boolean; $tabColor?: string; disabled?: boolean }>`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  border-bottom: 2px solid
    ${({ $active, $tabColor, disabled }) =>
      disabled
        ? "transparent"
        : $active
        ? $tabColor || COLOR_SYSTEM.get("Skyblue")?.[500]
        : "transparent"};
  border-top: none;
  border-left: none;
  border-right: none;
  color: ${({ $active, $tabColor, disabled }) =>
    disabled
      ? COLOR_SYSTEM.get("Gray")?.[400]
      : $active
      ? $tabColor || COLOR_SYSTEM.get("Skyblue")?.[500]
      : COLOR_SYSTEM.get("Gray")?.[500]};
  background: none;
  outline: none;
  transition: color 0.2s, border-color 0.2s;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  &:hover {
    color: ${({ $active, $tabColor, disabled }) =>
      disabled
        ? COLOR_SYSTEM.get("Gray")?.[400]
        : $active
        ? $tabColor || COLOR_SYSTEM.get("Skyblue")?.[500]
        : COLOR_SYSTEM.get("Gray")?.[700]};
    border-bottom: 2px solid
      ${({ $active, $tabColor, disabled }) =>
        disabled
          ? "transparent"
          : $active
          ? $tabColor || COLOR_SYSTEM.get("Skyblue")?.[500]
          : COLOR_SYSTEM.get("Gray")?.[300]};
  }
`;

const TabDivider = styled.div`
  width: 1px;
  height: 20px;
  background: ${COLOR_SYSTEM.get("Gray")?.[300]};
  margin: 0 4px;
  align-self: center;
`;

const FeedbackBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${COLOR_SYSTEM.get("Orange")?.[500]};
  margin-left: 2px;
  vertical-align: super;
  position: relative;
  top: -4px;
`;

const EvidenceContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 0;
  overflow-y: auto;

  /* 하이라이트된 참고문헌 요소 스타일 */
  .reference-highlight {
    background-color: ${COLOR_SYSTEM.get("Blue")?.[50]} !important;
    border-left: 4px solid ${COLOR_SYSTEM.get("Blue")?.[400]} !important;
    padding-left: 12px !important;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 102, 204, 0.1) !important;
  }

  /* 하이라이트 애니메이션 효과 */
  .reference-highlight-flash {
    animation: flashHighlight 1s ease-in-out;
  }

  @keyframes flashHighlight {
    0% {
      background-color: ${COLOR_SYSTEM.get("Blue")?.[100]};
    }
    50% {
      background-color: ${COLOR_SYSTEM.get("Blue")?.[50]};
    }
    100% {
      background-color: ${COLOR_SYSTEM.get("Blue")?.[50]};
    }
  }

  /* 검색 하이라이트 스타일 */
  .search-highlight {
    background-color: ${COLOR_SYSTEM.get("Yellow")?.[200]} !important;
    color: ${COLOR_SYSTEM.get("Gray")?.[900]} !important;
    font-weight: 600;
    border-radius: 2px;
    padding: 1px 2px;
  }

  .search-highlight.current {
    background-color: ${COLOR_SYSTEM.get("Orange")?.[300]} !important;
    box-shadow: 0 0 0 2px ${COLOR_SYSTEM.get("Orange")?.[500]};
  }
`;

// 제품 필터로 전체 해제 시 빈 상태 안내
const FilterEmptyHint = styled.div`
  padding: 24px 16px;
  text-align: center;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
`;

// 약 위치 정보 타입
interface DrugPosition {
  itemCode: string;
  element: HTMLElement;
  top: number;
  isVisible: boolean;
  visibilityRatio: number;
}

interface EvidenceSectionProps {
  referenceSnap?: Reference;
  selectedReferenceId?: string;
  message?: string;
  evaluation?: any; // AIReviewEvaluation 타입
  isEditMode?: boolean;
  onInsertReference?: (referenceId: string) => void;
  onHighlightedItemCodeChange?: (itemCode: string | null) => void;
  currentHighlightedItemCode?: string | null;
  onScrollToMedicationReady?: React.MutableRefObject<((itemCode: string) => void) | null>;
  // 피드백 관련 (Context에서 상태 가져옴)
  reviewId?: number;
  activeTab?: string;
  onTabChange?: (tabId: string | undefined) => void;
}

const EvidenceSection: React.FC<EvidenceSectionProps> = ({
  referenceSnap,
  selectedReferenceId,
  message,
  evaluation,
  isEditMode,
  onInsertReference,
  onHighlightedItemCodeChange,
  currentHighlightedItemCode,
  onScrollToMedicationReady,
  // 피드백 관련
  reviewId,
  activeTab: externalActiveTab,
  onTabChange,
}) => {
  log(LogLevel.VERBOSE, "EvidenceSection rendering/rerendering", {
    referenceSnap,
    selectedReferenceId,
  });

  // Context에서 피드백 상태 가져오기 (Provider 외부에서도 안전하게 사용)
  const feedbackContext = useFeedbackOptional();
  const phase = feedbackContext?.phase ?? "hidden";

  const [internalActiveTab, setInternalActiveTab] = useState<string>(evaluation ? "평가결과" : "허가사항");
  const [currentRefId, setCurrentRefId] = useState<string | undefined>(undefined);
  const [activeTabElement, setActiveTabElement] = useState<HTMLElement | null>(null);
  const [lastSearchTerm, setLastSearchTerm] = useState<string>("");
  // 검색 하이라이트(DOM 변형)가 떠 있는지. ref로 두어 필터 변경 핸들러에서 '동기'로 판단 →
  // setState commit 지연으로 리마운트를 놓치는 레이스를 차단. (렌더에는 쓰이지 않으므로 state 불필요)
  const searchActiveRef = useRef<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isScrollingFromClickRef = useRef<boolean>(false);
  const clickedItemCodeRef = useRef<string | null>(null);

  // 배열 기반 약 위치 관리
  const drugPositionsRef = useRef<DrugPosition[]>([]);
  const drugPositionsMapRef = useRef<Map<string, number>>(new Map()); // itemCode -> index 매핑

  // 제품(약) 필터 — 선택 itemCode 목록. null=전체/[]=해제. 허가사항·건기식 탭에만 적용.
  const [selectedItemCodes, setSelectedItemCodes] = useState<string[] | null>(null);
  // 코드(허가 항목) 필터 — 실제 코드 배열. null=전체(Phase 3, D15: []=해제 없음).
  const [selectedCodes, setSelectedCodes] = useState<string[] | null>(null);

  // 탭 콘텐츠 강제 리마운트 키. '검색 활성 중 필터를 바꿀 때만' 증가시켜, 검색이 변형한 DOM을
  // 비교 없이 폐기(3.4). 검색 시작/종료나 비검색 필터 변경에는 변하지 않아 검색 흐름을 깨지 않는다.
  const [remountKey, setRemountKey] = useState(0);

  // 제품+코드 필터 통합 커밋(applyFilters). 약 위치추적을 동기 초기화한 뒤 적용(D4):
  // 자식 handleHeaderMount가 부모 effect보다 먼저 실행돼 갓 적재한 항목을 지우는 시점 오류 방지.
  // 동등성 가드: 실제로 바뀔 때만 초기화·리마운트(불필요한 손상/리마운트 방지).
  const applyFilters = useCallback(
    (itemCodes: string[] | null, codes: string[] | null) => {
      const key = (v: string[] | null) => (v ? [...v].sort().join(",") : "all");
      const changed =
        key(selectedItemCodes) !== key(itemCodes) || key(selectedCodes) !== key(codes);
      if (!changed) return;
      drugPositionsRef.current = [];
      drugPositionsMapRef.current.clear();
      // 검색 하이라이트(DOM 변형)가 떠 있을 때만 리마운트로 충돌 차단. 비검색 변경은 memo로 가볍게.
      if (searchActiveRef.current) setRemountKey((k) => k + 1);
      setSelectedItemCodes(itemCodes);
      setSelectedCodes(codes);
    },
    [selectedItemCodes, selectedCodes]
  );

  // 필터 적용된 표시 대상(조건부 렌더로 검색 corpus 축소 → CSS-hide 함정 회피)
  const visibleDrugs = useMemo(
    () =>
      selectedItemCodes
        ? referenceSnap?.drugs?.filter((d) => selectedItemCodes.includes(d.itemCode))
        : referenceSnap?.drugs,
    [referenceSnap, selectedItemCodes]
  );
  const visibleHff = useMemo(
    () =>
      selectedItemCodes
        ? referenceSnap?.hff?.filter((d) => selectedItemCodes.includes(d.itemCode))
        : referenceSnap?.hff,
    [referenceSnap, selectedItemCodes]
  );

  // 외부에서 전달된 activeTab과 내부 상태 동기화
  const activeTab = externalActiveTab || internalActiveTab;
  const setActiveTab = (tab: string) => {
    setInternalActiveTab(tab);
    if (onTabChange) {
      onTabChange(tab);
    }
  };

  // 외부 activeTab 변경 시 내부 상태 동기화
  useEffect(() => {
    if (externalActiveTab && externalActiveTab !== internalActiveTab) {
      setInternalActiveTab(externalActiveTab);
    }
  }, [externalActiveTab]);

  // ReferenceFilterBar에 넘길 약 목록(코드 옵션 생성용 productLabels 동반).
  // 활성 탭 기준. 매 렌더 새 배열 생성을 막아 필터 내부 재계산을 줄인다.
  const filterDrugs = useMemo(() => {
    const src =
      activeTab === "허가사항"
        ? referenceSnap?.drugs
        : activeTab === "건기식"
        ? referenceSnap?.hff
        : undefined;
    return (src ?? []).map((d) => ({
      itemCode: d.itemCode,
      productName: d.productName,
      labels: d.productLabels ?? [],
    }));
  }, [activeTab, referenceSnap]);

  const changeActiveTab = (tab: string) => {
    setActiveTab(tab);
    // 탭 전환 시 필터 초기화(D8) — 허가사항 itemCode/코드 필터가 건기식 탭에서 매칭 0이 되는 혼선 방지
    applyFilters(null, null);
    eventLog("근거자료 탭 선택", { tab });
  };

  // 헤더 마운트 콜백 - 약 위치 정보 추가
  const handleHeaderMount = useCallback((itemCode: string, element: HTMLElement) => {
    // 이미 존재하는지 확인
    if (drugPositionsMapRef.current.has(itemCode)) {
      const index = drugPositionsMapRef.current.get(itemCode)!;
      drugPositionsRef.current[index].element = element;
      drugPositionsRef.current[index].top = element.offsetTop;
    } else {
      // 새로 추가
      const index = drugPositionsRef.current.length;
      drugPositionsRef.current.push({
        itemCode,
        element,
        top: element.offsetTop,
        isVisible: false,
        visibilityRatio: 0,
      });
      drugPositionsMapRef.current.set(itemCode, index);
    }

    // 위치 순서대로 정렬
    drugPositionsRef.current.sort((a, b) => a.top - b.top);

    // 인덱스 맵 재구성
    drugPositionsMapRef.current.clear();
    drugPositionsRef.current.forEach((pos, idx) => {
      drugPositionsMapRef.current.set(pos.itemCode, idx);
    });
  }, []);

  // 현재 보이는 약을 찾는 헬퍼 함수
  const findCurrentVisibleDrug = useCallback(() => {
    const visibleDrugs = drugPositionsRef.current.filter((p) => p.isVisible);
    
    if (visibleDrugs.length === 0) {
      return null;
    }
    
    // sticky 헤더를 고려한 선택 로직
    // ratio가 1인 것이 있으면 그것이 현재 sticky 헤더
    let selectedDrug = visibleDrugs.find((d) => d.visibilityRatio === 1);
    
    // ratio가 1인 것이 없으면, 가장 큰 ratio를 가진 약 선택
    if (!selectedDrug) {
      selectedDrug = visibleDrugs.reduce((prev, curr) =>
        curr.visibilityRatio > prev.visibilityRatio ? curr : prev
      );
    }
    
    return selectedDrug;
  }, []);

  // 최신 prop을 ref로 미러 — updateHighlightDebounced를 안정화(아래 deps에서 prop 제거)해
  // 스크롤(currentHighlightedItemCode 변경)마다 handleHeaderVisible가 새로 생겨 DrugLabelInfo가
  // 재렌더(html 재파싱)되는 것을 막는다.
  const onHighlightedItemCodeChangeRef = useRef(onHighlightedItemCodeChange);
  onHighlightedItemCodeChangeRef.current = onHighlightedItemCodeChange;
  const currentHighlightedItemCodeRef = useRef(currentHighlightedItemCode);
  currentHighlightedItemCodeRef.current = currentHighlightedItemCode;

  // 하이라이트 업데이트를 debounce 처리 (안정 — ref로 최신 prop을 읽음)
  const updateHighlightDebounced = useMemo(
    () =>
      debounce(() => {
        if (isScrollingFromClickRef.current) return;

        const cb = onHighlightedItemCodeChangeRef.current;
        const selectedDrug = findCurrentVisibleDrug();

        if (!selectedDrug) {
          cb?.(null);
          return;
        }
        if (cb && selectedDrug.itemCode !== currentHighlightedItemCodeRef.current) {
          cb(selectedDrug.itemCode);
        }
      }, 100),
    [findCurrentVisibleDrug]
  );

  // cleanup 시 debounce 취소
  useEffect(() => {
    return () => {
      updateHighlightDebounced.cancel();
    };
  }, [updateHighlightDebounced]);

  // 헤더 가시성 변경 콜백
  const handleHeaderVisible = useCallback(
    (itemCode: string, isVisible: boolean, ratio: number) => {
      if (!drugPositionsMapRef.current.has(itemCode)) {        return;
      }

      const index = drugPositionsMapRef.current.get(itemCode)!;
      const position = drugPositionsRef.current[index];

      position.isVisible = isVisible;
      position.visibilityRatio = ratio;
      // debounce된 하이라이트 업데이트 호출
      updateHighlightDebounced();
    },
    [updateHighlightDebounced]
  );

  // 클릭으로 스크롤할 때 특정 위치로 이동하는 함수
  const scrollToMedication = useCallback(
    (itemCode: string) => {
      // 약 위치 정보에서 element 찾기
      if (!drugPositionsMapRef.current.has(itemCode)) {        return;
      }

      const index = drugPositionsMapRef.current.get(itemCode)!;
      const position = drugPositionsRef.current[index];
      const medicationElement = position.element;

      // 스크롤 가능한 컨테이너 찾기
      const scrollableContainer = medicationElement.closest(
        '[data-tab-name="허가사항"][data-tab-active="true"]'
      );
      if (!scrollableContainer) {        return;
      }

      // 클릭 스크롤 플래그 설정
      isScrollingFromClickRef.current = true;
      clickedItemCodeRef.current = itemCode;

      // 스크롤 종료 시 플래그 리셋
      const handleScrollEnd = () => {
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
          isScrollingFromClickRef.current = false;
          clickedItemCodeRef.current = null;

          // 최종 하이라이트 업데이트
          if (onHighlightedItemCodeChange) {
            onHighlightedItemCodeChange(itemCode);
          }
        }, 150);
      };

      // scrollend 이벤트 리스너 추가 (지원하는 경우)
      const handleScrollEndEvent = () => {        isScrollingFromClickRef.current = false;
        clickedItemCodeRef.current = null;

        // 하이라이트 즉시 업데이트
        if (onHighlightedItemCodeChange) {
          onHighlightedItemCodeChange(itemCode);
        }

        // 이벤트 리스너 제거
        scrollableContainer.removeEventListener("scrollend", handleScrollEndEvent);
      };

      // scrollend 이벤트 지원 확인
      if ("onscrollend" in scrollableContainer) {
        scrollableContainer.addEventListener("scrollend", handleScrollEndEvent, { once: true });
      }

      // 스크롤 이벤트 리스너 추가 (폴백)
      const handleScroll = () => {
        handleScrollEnd();
      };

      scrollableContainer.addEventListener("scroll", handleScroll);

      // 스크롤 실행
      medicationElement.scrollIntoView({ behavior: "smooth", block: "start" });

      // 타임아웃 후 이벤트 리스너 제거
      setTimeout(() => {
        scrollableContainer.removeEventListener("scroll", handleScroll);
      }, 2000);
    },
    [onHighlightedItemCodeChange]
  );

  // scrollToMedication 함수를 부모 컴포넌트에 전달
  useEffect(() => {
    if (onScrollToMedicationReady) {
      onScrollToMedicationReady.current = scrollToMedication;
    }
  }, [scrollToMedication, onScrollToMedicationReady]);
  // 활성 탭 요소 가져오기
  const getActiveTabElement = (): HTMLElement | null => {
    if (!contentRef.current) return null;

    const activeTabContainer = contentRef.current.querySelector(
      `[data-tab-name="${activeTab}"][data-tab-active="true"]`
    );
    return activeTabContainer as HTMLElement;
  };

  // 활성 탭/리마운트 시 검색 대상 요소 업데이트.
  // remountKey를 의존성에 포함해, 검색 중 필터 리마운트 후 새 DOM을 검색 대상으로 다시 잡아
  // ContentSearchBar가 재검색하도록 한다(3.4). 검색 시작/종료엔 remountKey가 안 바뀌어 영향 없음.
  useEffect(() => {
    const element = getActiveTabElement();
    setActiveTabElement(element);
  }, [activeTab, remountKey, contentRef.current]);

  // 검색 상태 변경 핸들러
  const handleSearchStateChange = (isSearching: boolean, searchTerm: string) => {
    log(LogLevel.VERBOSE, "Search state changed", { isSearching, searchTerm, activeTab });

    // 검색 하이라이트 활성 여부 추적(동기) — 활성일 때만 필터 변경 시 리마운트(3.4)
    searchActiveRef.current = isSearching;

    // 실제로 새로운 검색이 시작될 때만 이벤트 로깅
    if (isSearching && searchTerm && searchTerm !== lastSearchTerm) {
      eventLog("근거자료 검색", { searchTerm, activeTab });
      setLastSearchTerm(searchTerm);
    }

    // 검색이 끝났을 때 (검색어가 비었을 때) 상태 초기화
    if (!searchTerm) {
      setLastSearchTerm("");
    }
  };

  // activeTab 변경 시 처리
  useEffect(() => {
    if (activeTab !== "허가사항") {      if (onHighlightedItemCodeChange) {
        onHighlightedItemCodeChange(null);
      }
    } else if (activeTab === "허가사항") {      // 약 위치 배열은 초기화하지 않음 - 이미 마운트된 약들의 정보를 유지

      // 탭 전환 후 현재 스크롤 위치에서 보이는 약을 찾아 하이라이트
      setTimeout(() => {
        if (drugPositionsRef.current.length > 0 && contentRef.current) {
          // 스크롤 가능한 컨테이너 찾기
          const scrollableContainer = contentRef.current.querySelector(
            '[data-tab-name="허가사항"][data-tab-active="true"]'
          ) as HTMLElement;

          if (scrollableContainer) {
            const scrollTop = scrollableContainer.scrollTop;
            const containerTop = scrollableContainer.getBoundingClientRect().top;

            // 현재 스크롤 위치에서 보이는 약 찾기
            let visibleDrug = null;

            for (const drug of drugPositionsRef.current) {
              if (drug.element) {
                const rect = drug.element.getBoundingClientRect();
                const relativeTop = rect.top - containerTop;

                // 헤더가 화면 상단 근처(sticky 위치)에 있는지 확인
                if (relativeTop >= -50 && relativeTop <= 100) {
                  visibleDrug = drug;
                  break;
                }
              }
            }

            // 보이는 약이 없으면 첫 번째 약 선택
            if (!visibleDrug && drugPositionsRef.current.length > 0) {
              visibleDrug = drugPositionsRef.current[0];
            }

            if (visibleDrug) {              if (onHighlightedItemCodeChange) {
                onHighlightedItemCodeChange(visibleDrug.itemCode);
              }
            }
          }
        }
      }, 150); // 컴포넌트들이 마운트되고 위치 정보가 수집될 시간을 줌
    }
  }, [activeTab, onHighlightedItemCodeChange]);

  // 컴포넌트 언마운트 시 하이라이트 정리
  useEffect(() => {
    return () => {
      if (contentRef.current) {
        const highlighted = contentRef.current.querySelectorAll(
          ".reference-highlight, .reference-highlight-flash"
        );
        highlighted.forEach((el) => {
          el.classList.remove("reference-highlight", "reference-highlight-flash");
        });
      }
      // 타이머 클리어
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      // 언마운트 시 하이라이트 제거
      if (onHighlightedItemCodeChange) {        onHighlightedItemCodeChange(null);
      }
    };
  }, [onHighlightedItemCodeChange]);

  // 목차 점프 — 보고서 ref와 동일 경로(currentRefId)로 처리. useCallback으로 안정화해
  // DrugLabelInfo(React.memo)가 매 렌더 재파싱되지 않게 함(인라인 함수면 memo가 깨져 전 약 재파싱).
  const handleTocJump = useCallback((id: string) => setCurrentRefId(id), []);

  useEffect(() => {
    if (selectedReferenceId) {
      // 참고문헌 ID로부터 문서 타입을 자동 결정
      const docType = getDocumentTypeFromRefId(selectedReferenceId);
      log(LogLevel.INFO, "Auto-detected document type from refId", {
        refId: selectedReferenceId,
        docType,
        targetTab: getTabNameFromDocType(docType) || "허가사항",
      });
      handleReferenceHighlight(selectedReferenceId, docType);
    }
  }, [selectedReferenceId]);

  const handleReferenceHighlight = (refId: string, docType?: any) => {
    // ref(본문 ⑆ 코드/의약품) 클릭 시 제품·코드 필터 모두 해제(3.5) — 필터로 가려진 약/항목이
    // 스크롤 대상에서 빠져 querySelector가 null이 되는 충돌 방지. 이미 전체면 bail-out.
    applyFilters(null, null);
    // [2025-06-09] 결정사항: 의약품 클릭 처리 로직 추가
    // medication- 접두사가 있는 경우 해당 탭으로 이동하고 의약품을 찾아 스크롤
    if (refId.startsWith("medication-")) {
      const itemCode = refId.replace("medication-", "");
      const targetTab = itemCode.startsWith("HFF") ? "건기식" : "허가사항";
      log(LogLevel.INFO, `Medication reference detected, switching to ${targetTab} tab`, { refId, itemCode });
      setActiveTab(targetTab);
      setCurrentRefId(refId);
      return;
    }

    const tabName = getTabNameFromDocType(docType);

    if (tabName) {
      log(LogLevel.INFO, "Switching to tab", { tab: tabName });
      setActiveTab(tabName);
    }
    setCurrentRefId(refId);
  };

  useEffect(() => {
    log(LogLevel.VERBOSE, "EvidenceSection activeTab changed", { activeTab });
  }, [activeTab]);

  useEffect(() => {
    log(LogLevel.VERBOSE, "EvidenceSection referenceSnap prop changed", { referenceSnap });
    // 보고서 교체 시 필터 초기화 — 이전 보고서 itemCode/코드가 남아 패널이 비는 혼선 방지.
    applyFilters(null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceSnap]);

  useEffect(() => {
    if (currentRefId && contentRef.current) {
      try {
        let isMedication = false;
        // [2025-06-09] 결정사항: 의약품 클릭 시 허가사항에서 해당 의약품 찾기 기능 추가
        if (currentRefId.startsWith("medication-")) {
          // medication- 접두사를 제거하고 의약품명 추출
          isMedication = true;
        }

        const elementId = currentRefId.startsWith("#") ? currentRefId.substring(1) : currentRefId;

        // 이전 하이라이트 제거
        const previousHighlighted = contentRef.current.querySelectorAll(
          ".reference-highlight, .reference-highlight-flash"
        );
        previousHighlighted.forEach((el) => {
          el.classList.remove("reference-highlight", "reference-highlight-flash");
          (el as HTMLElement).style.setProperty("background-color", "", "important");
          (el as HTMLElement).style.setProperty("border-left", "", "important");
        });

        // 새로운 요소 찾기 및 하이라이트
        const element = contentRef.current.querySelector(`#${elementId}`);
        if (element) {
          if (isMedication) {
            // medication 클릭 시 스크롤 플래그 설정
            isScrollingFromClickRef.current = true;
            const itemCode = currentRefId.replace("medication-", "");
            clickedItemCodeRef.current = itemCode;

            // 스크롤 종료 감지를 위한 타이머
            if (scrollTimeoutRef.current) {
              clearTimeout(scrollTimeoutRef.current);
            }

            scrollTimeoutRef.current = setTimeout(() => {              isScrollingFromClickRef.current = false;
              clickedItemCodeRef.current = null;

              // 스크롤 종료 후 해당 약 하이라이트
              if (onHighlightedItemCodeChange) {
                onHighlightedItemCodeChange(itemCode);
              }
            }, 1000); // smooth 스크롤 애니메이션 시간 고려

            // 두 번째 다음 형제 요소를 맨 위로 스크롤
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          } else {
            // 일반 reference 클릭 시에도 스크롤 플래그 설정
            isScrollingFromClickRef.current = true;

            // 스크롤 종료 감지를 위한 타이머
            if (scrollTimeoutRef.current) {
              clearTimeout(scrollTimeoutRef.current);
            }

            scrollTimeoutRef.current = setTimeout(() => {
              isScrollingFromClickRef.current = false;
              clickedItemCodeRef.current = null;

              // 허가사항 탭이면 현재 보이는 약을 찾아서 하이라이트
              if (activeTab === "허가사항" && onHighlightedItemCodeChange) {
                const selectedDrug = findCurrentVisibleDrug();

                if (selectedDrug) {
                  onHighlightedItemCodeChange(selectedDrug.itemCode);
                }
              }
            }, 1000); // smooth 스크롤 애니메이션 시간 고려

            // 두 번째 다음 형제 요소가 없으면 찾은 요소를 center로 스크롤
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            element.classList.add("reference-highlight", "reference-highlight-flash");
            // 가이드라인별 색상 적용
            const tabName = activeTab;
            const bg = getTabColor(tabName);
            if (bg) {
              (element as HTMLElement).style.setProperty(
                "background-color",
                bg + "20",
                "important"
              );
              (element as HTMLElement).style.setProperty(
                "border-left",
                `4px solid ${bg}`,
                "important"
              );
            }
          }
          // 하이라이트 적용

          // 플래시 애니메이션 후 플래시 클래스 제거 (하이라이트는 유지)
          setTimeout(() => {
            element.classList.remove("reference-highlight-flash");
          }, 1000);

          log(LogLevel.INFO, "Scrolled to element and applied highlight", { elementId });
        } else {
          log(LogLevel.WARNING, "Element to scroll to not found", { elementId });
        }
      } catch (error) {
        log(LogLevel.ERROR, "Error scrolling to element", { currentRefId, error });
      }
    }
  }, [currentRefId, activeTab]);

  const tabItems = useMemo(() => {
    const items: string[] = [];

    // 평가결과 탭을 맨 앞에 추가 (evaluation이 있을 때만)
    if (evaluation) {
      items.push("평가결과");
    }

    // 허가사항 탭 추가
    items.push("허가사항");

    // 건기식 탭 추가 (referenceSnap.hff가 있고 비어있지 않을 때)
    if (referenceSnap?.hff && referenceSnap.hff.length > 0) {
      items.push("건기식");
    }

    if (referenceSnap?.docs) {
      Object.keys(referenceSnap.docs).forEach((docKey) => {
        if (DOC_NAME[docKey]) {
          items.push(DOC_NAME[docKey]);
        } else {
          items.push(docKey);
        }
      });
    }

    // 피드백 탭을 마지막에 추가 (phase가 hidden이 아닐 때)
    if (phase !== "hidden") {
      items.push("피드백");
    }

    return items;
  }, [referenceSnap, evaluation, phase]);

  useEffect(() => {
    if (tabItems.length > 0 && !tabItems.includes(activeTab)) {
      setActiveTab(tabItems[0]);
    } else if (tabItems.length === 0 && activeTab !== "허가사항") {
      setActiveTab("허가사항");
    }
  }, [tabItems, activeTab]);

  if (!referenceSnap) {
    return (
      <SectionWrapper>
        <EvidenceTabsWrap>
          <EvidenceTabsScrollArea>
            <EvidenceTabs>
              <EvidenceTab
                $active={true}
                $tabColor={getTabColor("허가사항")}
                onClick={() => setActiveTab("허가사항")}
              >
                허가사항
              </EvidenceTab>
            </EvidenceTabs>
          </EvidenceTabsScrollArea>
        </EvidenceTabsWrap>
        <EvidenceContent>
          <TabContentWrapper isActive={true} tabName="허가사항">
            <Text textType="Body1">
              {message || "근거 자료 정보를 불러오는 중이거나, 데이터가 없습니다."}
            </Text>
          </TabContentWrapper>
        </EvidenceContent>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper>
      <EvidenceTabsWrap>
        <EvidenceTabsScrollArea>
          <EvidenceTabs>
            {tabItems.map((tabName, index) => {
              const isBeersTab = tabName === DOC_NAME.BEERS2023;
              const isFeedbackTab = tabName === "피드백";

              return (
                <React.Fragment key={tabName}>
                  {/* 피드백 탭 앞에 구분선 추가 */}
                  {isFeedbackTab && <TabDivider />}
                  <EvidenceTab
                    $active={activeTab === tabName}
                    $tabColor={getTabColor(tabName)}
                    disabled={isBeersTab}
                    onClick={() => {
                      if (isBeersTab) {
                        ToastManager.show({
                          content: "BEERS 콘텐츠를 표시할 수 없습니다.",
                          duration: 3000,
                        });
                        return;
                      }
                      changeActiveTab(tabName);
                    }}
                  >
                    {tabName}
                    {/* 피드백 탭에 상태 배지 표시 - phase가 detail일 때만 */}
                    {isFeedbackTab && phase === "detail" && <FeedbackBadge />}
                  </EvidenceTab>
                </React.Fragment>
              );
            })}
          </EvidenceTabs>
        </EvidenceTabsScrollArea>
      </EvidenceTabsWrap>

      {/* ContentSearchBar Molecule 사용 — 제품 필터를 같은 행(rightSlot)에 배치해 세로 공간 절약.
          필터는 허가사항·건기식 탭에만 노출(3.6) */}
      <ContentSearchBar
        targetElement={activeTabElement}
        placeholder="근거자료에서 검색..."
        onSearchStateChange={handleSearchStateChange}
        rightSlot={
          activeTab === "허가사항" || activeTab === "건기식" ? (
            <ReferenceFilterBar
              // key: 탭 전환·보고서 교체 시 팝오버 닫고 draft 폐기(D16)
              key={`${activeTab}:${reviewId ?? ""}`}
              drugs={filterDrugs}
              moduleNameMap={activeTab === "건기식" ? HFF_MODULE_NAME_MAP : undefined}
              selectedItemCodes={selectedItemCodes}
              selectedCodes={selectedCodes}
              onApply={({ itemCodes, codes }) => {
                applyFilters(itemCodes, codes);
                eventLog("근거자료 필터", {
                  activeTab,
                  productCount: itemCodes ? itemCodes.length : null,
                  codeCount: codes ? codes.length : null,
                });
              }}
            />
          ) : undefined
        }
      />

      <EvidenceContent ref={contentRef}>
        {/* 편집 모드에서 텍스트 선택 시 참조 삽입 버튼 표시 */}
        <SelectionBasedReferenceButton
          containerRef={contentRef}
          isEditMode={isEditMode || false}
          onInsertReference={onInsertReference}
        />
        {tabItems.map((tabName) => {
          const docKey = Object.keys(DOC_NAME).find((key) => DOC_NAME[key] === tabName) as
            | keyof typeof DOC_NAME
            | undefined;
          let content = null;

          if (tabName === "평가결과") {
            content = <EvaluationResult evaluation={evaluation} />;
          } else if (tabName === "허가사항") {
            content = visibleDrugs && visibleDrugs.length === 0 ? (
              <FilterEmptyHint>필터에서 표시할 약을 선택하세요.</FilterEmptyHint>
            ) : (
              visibleDrugs?.map((drug, index) => (
              <div key={`${drug.itemCode || "drug"}-${index}`} data-item-code={drug.itemCode}>
                <DrugLabelInfo
                  labelInfo={drug.productLabels}
                  itemCode={drug.itemCode}
                  productName={drug.productName}
                  showMetadata={false}
                  selectedCodes={selectedCodes ?? undefined}
                  onTocJump={handleTocJump}
                  onHeaderMount={handleHeaderMount}
                  onHeaderVisible={handleHeaderVisible}
                />
              </div>
              ))
            );
          } else if (tabName === "건기식") {
            content = visibleHff && visibleHff.length === 0 ? (
              <FilterEmptyHint>필터에서 표시할 약을 선택하세요.</FilterEmptyHint>
            ) : (
              visibleHff?.map((drug, index) => (
              <div key={`hff-${drug.itemCode || "drug"}-${index}`} data-item-code={drug.itemCode}>
                <DrugLabelInfo
                  labelInfo={drug.productLabels}
                  itemCode={drug.itemCode}
                  productName={drug.productName}
                  showMetadata={false}
                  moduleNameMap={HFF_MODULE_NAME_MAP}
                  selectedCodes={selectedCodes ?? undefined}
                  onTocJump={handleTocJump}
                />
              </div>
              ))
            );
          } else if (tabName === "피드백" && reviewId) {
            content = (
              <AIReviewFeedbackForm
                reviewId={reviewId}
              />
            );
          } else if (docKey === "BEERS2023" && tabName === DOC_NAME.BEERS2023) {
            content = <BEERS2023 />;
          } else if (docKey === "CCMM" && tabName === DOC_NAME.CCMM) {
            content = <CCMM />;
          } else if (docKey === "LCMM" && tabName === DOC_NAME.LCMM) {
            content = <LCMM />;
          } else if (docKey === "SPSTv3" && tabName === DOC_NAME.SPSTv3) {
            content = <SPSTv3 />;
          } else if (docKey === "POLY" && tabName === DOC_NAME.POLY) {
            content = <POLY />;
          } else if (docKey === "KABS" && tabName === DOC_NAME.KABS) {
            content = <KABS />;
          } else if (docKey === "KDGERI" && tabName === DOC_NAME.KDGERI) {
            content = <KDGERI />;
          } else {
            content = <Text textType="Body1">{`${tabName} 내용 표시 예정입니다.`}</Text>;
          }

          // 허가사항·건기식 탭은 remountKey로 리마운트(검색 중 필터 변경 시에만 증가, 3.4·D5).
          // 검색 시작/종료나 비검색 필터 변경엔 remountKey 불변 → 리마운트 없음(검색 흐름 보존 + 빠른 토글).
          const wrapperKey =
            tabName === "허가사항" || tabName === "건기식"
              ? `${tabName}:${remountKey}`
              : tabName;
          return (
            <TabContentWrapper key={wrapperKey} isActive={activeTab === tabName} tabName={tabName}>
              {content}
            </TabContentWrapper>
          );
        })}
      </EvidenceContent>
    </SectionWrapper>
  );
};

export default EvidenceSection;
